import React, { useState } from 'react'
import { HeaderContent, HomeStyles } from '../styles'
import { Row } from '../../../../components/commons'
import ModalCreateFolder from '../../../../components/Modals/ModalCreateFolder'
import { useDirectory } from '../../../../store/directory'
import { useUserStore } from '../../../../store/user'
import { usePermissions } from '../../../../hooks/usePermissions'
import ModalUpload from '../../../../components/Modals/ModalUpload'
import ModalDelete from '../../../../components/Modals/ModalDelete'
import ModalCopyFolder from '../../../../components/Modals/ModalCopyFolder'
import ModalBatchSign, { ModalBatchSignHandle } from '../../../../components/Modals/ModalBatchSign'
import { api } from '../../../../services/api'
import useToast from '../../../../hooks/useToast'
import { Tooltip } from 'react-tooltip'
import { FaChevronRight, FaDownload, FaPlus, FaTrash, FaUpload, FaFolderOpen, FaFileSignature, FaFolder } from 'react-icons/fa6'
import { ButtonRound } from '../../../../components/Header/styles'
import UserPerMission from '../UsersPermission'
import { useTheme } from 'styled-components'
import axios from 'axios'
import { ResponseError } from '../../../../validators/utils.interface'

export interface BreadCrumbProps {
  refetch: () => void
  fileRefs?: any
}

type CreateModalRef = React.ElementRef<typeof ModalCreateFolder>
type ModalUploadRef = React.ElementRef<typeof ModalUpload>
type ModalDeleteRef = React.ElementRef<typeof ModalDelete>
type ModalCopyFolderRef = React.ElementRef<typeof ModalCopyFolder>

const BreadCrumb: React.FunctionComponent<BreadCrumbProps> = ({ refetch, fileRefs }) => {
  const modalCreateFolder = React.useRef<CreateModalRef>(null)
  const modalRef = React.useRef<ModalUploadRef>(null)
  const modalDeleteRef = React.useRef<ModalDeleteRef>(null)
  const modalCopyFolderRef = React.useRef<ModalCopyFolderRef>(null)
  const modalBatchSignRef = React.useRef<ModalBatchSignHandle>(null)

  const [isLoading, setisLoading] = React.useState(false)

  const toast = useToast()
  const { user } = useUserStore()
  const { canCreateDocuments, canDeleteDocuments, canEditDocuments } = usePermissions()

  const isManagerOrAdmin = user?.role === 'admin' || user?.role === 'manager' || (user as any)?.manager

  const directory = useDirectory((state) => state.directory)

  // Verificar permissões baseadas em grupo ao invés de role do usuário
  const canCreate = canCreateDocuments
  const canDelete = canDeleteDocuments
  const canEdit = canEditDocuments

  const onSelectFolder = (index: number) => {
    let directories = [...directory]

    directories.splice(index + 1)

    useDirectory.getState().setDirectory(directories)
  }

  const handleDownload = async () => {
    let selectedDocuments: any = []

    fileRefs.current.map((ref: any) => {
      if (ref?.current?.checked) {
        selectedDocuments.push({ 
          _id: ref.current?._id, 
          name: ref.current.name,
          isFolder: ref.current.isFolder 
        })
      }
    })

    if (!selectedDocuments.length) return toast.errorToast('Selecione um documento!')

    // Verificar se há pastas selecionadas
    const hasFolders = selectedDocuments.some((doc: any) => doc.isFolder)
    if (hasFolders) return toast.errorToast('Não é possível baixar pastas! Selecione apenas documentos.')

    await Promise.all(
      selectedDocuments.map(async (doc: any) => {
        try {
          // Obter link de compartilhamento do Dropbox
          const { data: response } = await api.get(`/documents/${doc._id}`)
          
          if (response.success && response.shareLink) {
            // Adicionar parâmetro dl=1 para download direto
            const downloadUrl = response.shareLink.includes('?') 
              ? `${response.shareLink}&dl=1`
              : `${response.shareLink}?dl=1`
            
            // Criar link temporário para download
            const link: any = document.createElement('a')
            link.href = downloadUrl
            link.download = doc.name
            link.target = '_blank'
            
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          } else {
            toast.errorToast(`Erro ao obter link para ${doc.name}`)
          }
        } catch (error) {
          toast.errorToast(`Erro ao baixar ${doc.name}`)
        }
        return true
      }),
    )

    toast.successToast('Downloads iniciados com sucesso')
  }

  const onDeleteFoldes = async (ids: any) => {
    try {
      setisLoading(true)

      await Promise.all(
        ids.map(async (id: any) => {
          await api.delete(`/documents/${id}`)
        }),
      )

      refetch()
      modalDeleteRef.current?.closeModal()
      toast.successToast('Todos arquivos foram deletados')
    } catch (error) {
      const typedError = error as ResponseError
      if (axios.isAxiosError(error)) {
        console.log(typedError)
        toast.errorToast(typedError?.response?.data?.message)
      } else {
        toast.errorToast('Erro ao deletar o registro !')
      }
    } finally {
      setisLoading(false)
      modalDeleteRef.current?.closeModal()
    }
  }

  const onPressDelete = () => {
    let ids: any = []

    fileRefs.current.map((ref: any) => {
      

      if (ref?.current?.checked) {
        ids.push(ref.current._id)
      }
    })
    if (ids.length > 0) {
      modalDeleteRef.current?.openModal(ids)
    } else {
      toast.errorToast('Selecione um arquivo')
    }
  }

  const [selectedItems, setSelectedItems] = useState<any[]>([])

  const onPressMove = () => {
    let items: any = []

    fileRefs.current.map((ref: any) => {
      if (ref?.current?.checked) {
        items.push({ 
          _id: ref.current?._id, 
          name: ref.current.name,
          isFolder: ref.current.isFolder 
        })
      }
    })

    if (!items.length) {
      return toast.errorToast('Selecione um documento ou pasta para mover!')
    }

    setSelectedItems(items)
    modalCopyFolderRef.current?.openModal()
  }

  const onPressBatchSign = () => {
    let items: any = []

    fileRefs.current.map((ref: any) => {
      if (ref?.current?.checked) {
        items.push({ 
          _id: ref.current?._id, 
          name: ref.current.name,
          isFolder: ref.current.isFolder 
        })
      }
    })

    if (!items.length) {
      return toast.errorToast('Selecione os documentos que deseja conferir e assinar!')
    }

    const hasOnlyFolders = items.every((i: any) => i.isFolder)
    if (hasOnlyFolders) {
      return toast.errorToast('Selecione arquivos PDF. Pastas não podem ser assinadas.')
    }

    modalBatchSignRef.current?.openModal(items)
  }

  const theme: any = useTheme()
  const primaryColor = theme?.colors?.primary || '#382b7c'

  const iconActionBtnStyle: React.CSSProperties = {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: 8,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  }

  return (
    <>
      <Tooltip
        id='my-tooltip'
        style={{ backgroundColor: '#1e293b', zIndex: 9999, color: 'white', borderRadius: 6, fontSize: 12, padding: '4px 8px' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        {/* Breadcrumb navegável à esquerda */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {directory.map((bread, index) => {
            const isLast = index === directory.length - 1
            return (
              <div key={bread.name + index} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => onSelectFolder(index)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: isLast ? 'default' : 'pointer',
                    color: isLast ? '#0f172a' : '#64748b',
                    fontWeight: isLast ? 700 : 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 6px',
                    borderRadius: 6,
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isLast) e.currentTarget.style.backgroundColor = '#f1f5f9'
                  }}
                  onMouseLeave={(e) => {
                    if (!isLast) e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  <FaFolder style={{ color: isLast ? '#f59e0b' : '#94a3b8', fontSize: 14 }} />
                  <span>{bread.name}</span>
                </button>
                {!isLast && <FaChevronRight size={10} color="#94a3b8" />}
              </div>
            )
          })}
        </div>

        {/* Toolbar de Ações organizada à direita */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Botão de Enviar Documento (Ação Principal) */}
          {canCreate && (
            <button
              type="button"
              onClick={() => modalRef.current?.openModal()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: primaryColor,
                color: '#ffffff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                transition: 'opacity 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <FaUpload size={13} />
              <span>Enviar Documento</span>
            </button>
          )}

          {/* Botão de Nova Pasta */}
          {canCreate && (
            <button
              type="button"
              onClick={() => modalCreateFolder.current?.openModal()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255, 255, 255, 0.5)',
                color: '#334155',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.25)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)'
              }}
            >
              <FaPlus size={12} />
              <span>Nova Pasta</span>
            </button>
          )}

          {/* Botão de Assinatura Digital ICP-Brasil */}
          {isManagerOrAdmin && (
            <button
              type="button"
              onClick={onPressBatchSign}
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Conferir e Assinar Selecionados com Certificado ICP-Brasil"
              data-tooltip-place="top"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: '#107c41',
                color: '#ffffff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(16, 124, 65, 0.2)',
                transition: 'opacity 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <FaFileSignature size={14} />
              <span>Assinar ICP-Brasil</span>
            </button>
          )}

          {/* Grupo de Ações Secundárias (Download, Mover, Permissões, Excluir) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderLeft: '1px solid rgba(0, 0, 0, 0.12)', paddingLeft: 8 }}>
            <button
              type="button"
              onClick={() => handleDownload()}
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Baixar selecionados"
              data-tooltip-place="top"
              style={iconActionBtnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                e.currentTarget.style.color = '#0f172a'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'
                e.currentTarget.style.color = '#475569'
              }}
            >
              <FaDownload size={13} />
            </button>

            {canEdit && (
              <button
                type="button"
                onClick={onPressMove}
                data-tooltip-id="my-tooltip"
                data-tooltip-content="Mover selecionados"
                data-tooltip-place="top"
                style={iconActionBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                  e.currentTarget.style.color = '#0f172a'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'
                  e.currentTarget.style.color = '#475569'
                }}
              >
                <FaFolderOpen size={13} />
              </button>
            )}

            {canEdit && <UserPerMission fileRefs={fileRefs} onSubmitButton={() => {}} />}

            {canDelete && (
              <button
                type="button"
                onClick={() => onPressDelete()}
                data-tooltip-id="my-tooltip"
                data-tooltip-content="Excluir selecionados"
                data-tooltip-place="top"
                style={{
                  ...iconActionBtnStyle,
                  borderColor: '#fecaca',
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef2f2'
                }}
              >
                <FaTrash size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      <ModalCopyFolder refetch={refetch} ref={modalCopyFolderRef} selectedItems={selectedItems} />
      <ModalCreateFolder refetch={refetch} ref={modalCreateFolder} />
      <ModalUpload refetch={refetch} ref={modalRef} />
      <ModalBatchSign ref={modalBatchSignRef} refetch={refetch} />
      <ModalDelete
        loading={isLoading}
        title='Deseja deletar esses arquivo(s)?'
        onSubmitButton={onDeleteFoldes}
        ref={modalDeleteRef}
      />
    </>
    //<ModalCopyFolder refetch={refetch} ref={modalCopyFolderRef} />
    //<ModalCreateFolder refetch={refetch} ref={modalCreateFolder} />
    //<ModalUpload refetch={refetch} ref={modalRef} />
  )
}

export default BreadCrumb
